<template>
  <div class="puzzle-wrap">
    <!-- Header -->
    <div class="header">
      <button class="back-btn" @click="goBack">← بازگشت</button>
      <h1 class="title">پازل 🧩</h1>
      <div class="diff-pills" v-if="phase === 'select'">
        <button 
          v-for="d in difficulties" 
          :key="d.key" 
          class="diff-pill" 
          :class="{ active: difficulty === d.key }"
          @click="setDifficulty(d.key)"
        >
          {{ d.label }}
        </button>
      </div>
    </div>

    <!-- Image Selection Phase -->
    <div v-if="phase === 'select'" class="select-phase">
      <div class="source-tabs">
        <button 
          class="source-tab" 
          :class="{ active: imageSource === 'models' }"
          @click="imageSource = 'models'"
        >
          🖼️ تصاویر آماده
        </button>
        <button 
          class="source-tab" 
          :class="{ active: imageSource === 'ai' }"
          @click="imageSource = 'ai'"
        >
          🤖 گفتگو با هوش مصنوعی
        </button>
        <button 
          class="source-tab" 
          :class="{ active: imageSource === 'upload' }"
          @click="imageSource = 'upload'"
        >
          📤 آپلود تصویر
        </button>
      </div>

      <!-- Model Images -->
      <div v-if="imageSource === 'models'" class="models-grid">
        <div 
          v-for="m in modelImages" 
          :key="m.id" 
          class="model-card"
          :class="{ selected: selectedModel?.id === m.id }"
          @click="selectModel(m)"
        >
          <div class="model-preview" :style="{ background: m.bg }">
            <span class="model-emoji">{{ m.emoji }}</span>
          </div>
          <div class="model-name">{{ m.name }}</div>
        </div>
      </div>

      <!-- AI Chat -->
      <div v-if="imageSource === 'ai'" class="ai-chat">
        <div class="chat-messages" ref="chatMessagesRef">
          <div v-for="(msg, i) in chatMessages" :key="i" class="chat-msg" :class="msg.role">
            <div class="msg-bubble">{{ msg.text }}</div>
          </div>
        </div>
        <div class="chat-input-row">
          <input 
            v-model="chatInput" 
            class="chat-input" 
            placeholder="بگو چی دوست داری... مثلاً یک گربه خوشگل 🐱"
            @keyup.enter="sendChat"
          />
          <button class="chat-send" @click="sendChat" :disabled="aiLoading">
            {{ aiLoading ? '...' : '🚀' }}
          </button>
        </div>
        <div v-if="suggestedModels.length" class="suggested-models">
          <p class="suggest-label">این‌ها چطورن؟ 👇</p>
          <div class="models-row">
            <div 
              v-for="m in suggestedModels" 
              :key="m.id" 
              class="model-card small"
              @click="selectModel(m)"
            >
              <div class="model-preview" :style="{ background: m.bg }">
                <span class="model-emoji">{{ m.emoji }}</span>
              </div>
              <div class="model-name">{{ m.name }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload -->
      <div v-if="imageSource === 'upload'" class="upload-zone">
        <input type="file" accept="image/*" @change="onFileUpload" ref="fileInputRef" class="file-input" />
        <div class="upload-box" @click="triggerUpload" @dragover.prevent @drop.prevent="onDrop">
          <div v-if="!uploadedImage" class="upload-placeholder">
            <span class="upload-icon">📷</span>
            <p>یک تصویر انتخاب کن یا اینجا رها کن</p>
          </div>
          <img v-else :src="uploadedImage" class="upload-preview" />
        </div>
      </div>

      <!-- Start Button -->
      <button 
        class="start-btn" 
        :disabled="!canStart"
        @click="startGame"
      >
        شروع پازل! 🎮
      </button>
    </div>

    <!-- Game Phase -->
    <div v-if="phase === 'playing'" class="game-phase">
      <div class="game-stats">
        <div class="stat-box">
          <span class="stat-icon">⏱️</span>
          <span class="stat-value">{{ timeText }}</span>
        </div>
        <div class="stat-box">
          <span class="stat-icon">🎯</span>
          <span class="stat-value">{{ moves }} حرکت</span>
        </div>
        <button 
          class="preview-btn" 
          @mousedown="showPreview = true" 
          @mouseup="showPreview = false" 
          @mouseleave="showPreview = false"
          @touchstart.prevent="showPreview = true"
          @touchend.prevent="showPreview = false"
        >
          👁️ پیش‌نمایش
        </button>
      </div>

      <div class="game-area" ref="gameAreaRef">
        <!-- Target Board with slots and placed pieces -->
        <div class="puzzle-board" :style="boardStyle" ref="puzzleBoardRef">
          <!-- Grid slots -->
          <div 
            v-for="slot in gridSlots" 
            :key="'slot-'+slot.id" 
            class="puzzle-slot"
            :style="slotStyle(slot)"
          ></div>
          
          <!-- Placed pieces (on board) -->
          <div 
            v-for="piece in placedPieces" 
            :key="'placed-'+piece.id" 
            class="puzzle-piece placed"
            :style="placedPieceStyle(piece)"
          >
            <img 
              v-if="piece.imageUrl" 
              :src="piece.imageUrl" 
              class="piece-image"
              draggable="false"
            />
          </div>
          
          <!-- Preview overlay -->
          <div v-if="showPreview" class="preview-overlay">
            <img :src="gameImage" class="preview-image" />
          </div>
        </div>

        <!-- Pieces Tray - using flexbox grid -->
        <div class="pieces-tray">
          <div 
            v-for="piece in trayPieces" 
            :key="'tray-'+piece.id" 
            class="tray-piece"
            :class="{ dragging: draggingPiece?.id === piece.id }"
            :style="trayPieceStyle(piece)"
            @pointerdown="onPieceDown($event, piece)"
          >
            <img 
              v-if="piece.imageUrl" 
              :src="piece.imageUrl" 
              class="piece-image"
              draggable="false"
            />
          </div>
        </div>
      </div>

      <!-- Dragging piece (follows cursor) -->
      <div 
        v-if="draggingPiece" 
        class="dragging-piece"
        :style="draggingStyle"
      >
        <img 
          v-if="draggingPiece.imageUrl" 
          :src="draggingPiece.imageUrl" 
          class="piece-image"
          draggable="false"
        />
      </div>
    </div>

    <!-- Completion Popup -->
    <div v-if="showComplete" class="complete-overlay">
      <div class="complete-popup">
        <div class="confetti">🎉</div>
        <h2>آفرین! پازل رو کامل کردی! 🌟</h2>
        <p class="complete-stats">
          زمان: {{ timeText }} • حرکت‌ها: {{ moves }}
        </p>
        <div class="complete-actions">
          <button class="action-btn replay" @click="replayGame">🔄 بازی دوباره</button>
          <button class="action-btn choose" @click="chooseAnother">🖼️ تصویر دیگه</button>
          <button class="action-btn back" @click="goBack">🏠 بازگشت به بازی‌ها</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation } from '@vue/apollo-composable'
import { SUBMIT_PUZZLE_MUTATION } from '../api/mutations'
import { useToast } from '../ui/toast'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const slug = route.params.slug

// Phases: 'select' | 'playing'
const phase = ref('select')
const difficulty = ref('easy')
const imageSource = ref('models')

const difficulties = [
  { key: 'easy', label: 'آسان', grid: 2 },
  { key: 'hard', label: 'سخت', grid: 3 },
  { key: 'superhard', label: 'خیلی سخت', grid: 4 }
]

// Model images
const modelImages = reactive([
  { id: 1, name: 'شیر', emoji: '🦁', bg: 'linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)', tags: ['animal', 'lion'] },
  { id: 2, name: 'فیل', emoji: '🐘', bg: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)', tags: ['animal', 'elephant'] },
  { id: 3, name: 'پروانه', emoji: '🦋', bg: 'linear-gradient(135deg, #c084fc 0%, #7c3aed 100%)', tags: ['animal', 'butterfly'] },
  { id: 4, name: 'ماهی', emoji: '🐠', bg: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)', tags: ['animal', 'fish'] },
  { id: 5, name: 'رنگین‌کمان', emoji: '🌈', bg: 'linear-gradient(135deg, #f87171 0%, #fbbf24 25%, #34d399 50%, #60a5fa 75%, #a78bfa 100%)', tags: ['nature', 'rainbow'] },
  { id: 6, name: 'گل', emoji: '🌸', bg: 'linear-gradient(135deg, #fda4af 0%, #f43f5e 100%)', tags: ['nature', 'flower'] },
  { id: 7, name: 'درخت', emoji: '🌳', bg: 'linear-gradient(135deg, #86efac 0%, #22c55e 100%)', tags: ['nature', 'tree'] },
  { id: 8, name: 'خورشید', emoji: '☀️', bg: 'linear-gradient(135deg, #fef08a 0%, #facc15 100%)', tags: ['nature', 'sun'] },
  { id: 9, name: 'ماشین', emoji: '🚗', bg: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)', tags: ['vehicle', 'car'] },
  { id: 10, name: 'موشک', emoji: '🚀', bg: 'linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)', tags: ['vehicle', 'rocket', 'space'] },
  { id: 11, name: 'هواپیما', emoji: '✈️', bg: 'linear-gradient(135deg, #67e8f9 0%, #06b6d4 100%)', tags: ['vehicle', 'airplane'] },
  { id: 12, name: 'قطار', emoji: '🚂', bg: 'linear-gradient(135deg, #a3a3a3 0%, #525252 100%)', tags: ['vehicle', 'train'] },
  { id: 13, name: 'گربه', emoji: '🐱', bg: 'linear-gradient(135deg, #fdba74 0%, #f97316 100%)', tags: ['animal', 'cat'] },
  { id: 14, name: 'سگ', emoji: '🐶', bg: 'linear-gradient(135deg, #d4a373 0%, #9a5d2f 100%)', tags: ['animal', 'dog'] },
  { id: 15, name: 'خانه', emoji: '🏠', bg: 'linear-gradient(135deg, #fcd9b8 0%, #ea580c 100%)', tags: ['building', 'house'] },
  { id: 16, name: 'ستاره', emoji: '⭐', bg: 'linear-gradient(135deg, #fef9c3 0%, #eab308 100%)', tags: ['space', 'star'] },
])

const selectedModel = ref(null)

// AI Chat
const chatMessages = reactive([
  { role: 'ai', text: 'سلام دوست کوچولو! 👋 بگو چه تصویری دوست داری برای پازلت؟' }
])
const chatInput = ref('')
const aiLoading = ref(false)
const suggestedModels = ref([])
const chatMessagesRef = ref(null)

// Upload
const uploadedImage = ref(null)
const fileInputRef = ref(null)

// Game state
const gameImage = ref(null)
const pieces = reactive([])
const gridSlots = reactive([])
const moves = ref(0)
const seconds = ref(0)
const showPreview = ref(false)
const showComplete = ref(false)
const draggingPiece = ref(null)
const dragPosition = reactive({ x: 0, y: 0 })
const gameAreaRef = ref(null)
const puzzleBoardRef = ref(null)
let timerInterval = null

const { mutate: submitPuzzle } = useMutation(SUBMIT_PUZZLE_MUTATION)

// Computed
const gridSize = computed(() => {
  const d = difficulties.find(x => x.key === difficulty.value)
  return d ? d.grid : 2
})

const boardSizeValue = computed(() => {
  return Math.min(340, window.innerWidth - 40)
})

const pieceSizeValue = computed(() => {
  return Math.floor(boardSizeValue.value / gridSize.value)
})

const boardStyle = computed(() => ({
  width: boardSizeValue.value + 'px',
  height: boardSizeValue.value + 'px'
}))

const timeText = computed(() => {
  const m = Math.floor(seconds.value / 60).toString().padStart(2, '0')
  const s = (seconds.value % 60).toString().padStart(2, '0')
  return `${m}:${s}`
})

const canStart = computed(() => {
  if (imageSource.value === 'models') return !!selectedModel.value
  if (imageSource.value === 'upload') return !!uploadedImage.value
  if (imageSource.value === 'ai') return !!selectedModel.value
  return false
})

// Separate pieces into tray and placed
const trayPieces = computed(() => pieces.filter(p => !p.isPlaced))
const placedPieces = computed(() => pieces.filter(p => p.isPlaced))

const draggingStyle = computed(() => {
  const size = pieceSizeValue.value
  return {
    width: size + 'px',
    height: size + 'px',
    left: (dragPosition.x - size/2) + 'px',
    top: (dragPosition.y - size/2) + 'px'
  }
})

// Methods
function goBack() {
  router.push(`/room/${slug}`)
}

function setDifficulty(d) {
  difficulty.value = d
}

function selectModel(m) {
  selectedModel.value = m
}

// AI Chat handling
async function sendChat() {
  const text = chatInput.value.trim()
  if (!text || aiLoading.value) return
  
  chatMessages.push({ role: 'kid', text })
  chatInput.value = ''
  aiLoading.value = true
  
  await nextTick()
  if (chatMessagesRef.value) {
    chatMessagesRef.value.scrollTop = chatMessagesRef.value.scrollHeight
  }
  
  const keywords = text.toLowerCase().split(/\s+/)
  const matches = modelImages.filter(m => {
    const allTags = [...m.tags, m.name.toLowerCase()]
    return keywords.some(kw => allTags.some(tag => tag.includes(kw) || kw.includes(tag)))
  })
  
  await new Promise(r => setTimeout(r, 800))
  
  if (matches.length > 0) {
    suggestedModels.value = matches.slice(0, 4)
    chatMessages.push({ 
      role: 'ai', 
      text: `عالیه! ${text} خیلی قشنگه! 😍 من این‌ها رو برات پیدا کردم. کدومو بیشتر دوست داری؟` 
    })
  } else {
    const shuffled = [...modelImages].sort(() => Math.random() - 0.5)
    suggestedModels.value = shuffled.slice(0, 4)
    chatMessages.push({ 
      role: 'ai', 
      text: 'چه ایده جالبی! این تصویرها رو ببین، شاید یکیشون خوشت بیاد! 🌟' 
    })
  }
  
  aiLoading.value = false
  await nextTick()
  if (chatMessagesRef.value) {
    chatMessagesRef.value.scrollTop = chatMessagesRef.value.scrollHeight
  }
}

// Upload handling
function triggerUpload() {
  fileInputRef.value?.click()
}

function onFileUpload(e) {
  const file = e.target.files?.[0]
  if (file) loadFile(file)
}

function onDrop(e) {
  const file = e.dataTransfer?.files?.[0]
  if (file) loadFile(file)
}

function loadFile(file) {
  if (!file.type.startsWith('image/')) {
    toast?.show({ text: 'فقط تصویر قابل قبول است', type: 'error' })
    return
  }
  const reader = new FileReader()
  reader.onload = (e) => {
    uploadedImage.value = e.target.result
  }
  reader.readAsDataURL(file)
}

function generateModelImage(model) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const size = 400
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    
    const gradient = ctx.createLinearGradient(0, 0, size, size)
    const colors = model.bg.match(/#[a-f0-9]{6}/gi) || ['#ffffff', '#cccccc']
    gradient.addColorStop(0, colors[0])
    gradient.addColorStop(1, colors[colors.length - 1] || colors[0])
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, size, size)
    
    ctx.font = '200px serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(model.emoji, size/2, size/2)
    
    ctx.font = '50px serif'
    ctx.fillText('✨', 50, 50)
    ctx.fillText('⭐', size - 50, 60)
    ctx.fillText('💫', 40, size - 50)
    ctx.fillText('🌟', size - 50, size - 50)
    
    resolve(canvas.toDataURL('image/png'))
  })
}

function generatePieceImage(fullImageSrc, row, col, grid) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const srcPieceSize = img.width / grid
      const dstPieceSize = pieceSizeValue.value
      
      const canvas = document.createElement('canvas')
      canvas.width = dstPieceSize
      canvas.height = dstPieceSize
      const ctx = canvas.getContext('2d')
      
      const sx = col * srcPieceSize
      const sy = row * srcPieceSize
      
      // Rounded corners
      const r = 6
      ctx.beginPath()
      ctx.moveTo(r, 0)
      ctx.lineTo(dstPieceSize - r, 0)
      ctx.quadraticCurveTo(dstPieceSize, 0, dstPieceSize, r)
      ctx.lineTo(dstPieceSize, dstPieceSize - r)
      ctx.quadraticCurveTo(dstPieceSize, dstPieceSize, dstPieceSize - r, dstPieceSize)
      ctx.lineTo(r, dstPieceSize)
      ctx.quadraticCurveTo(0, dstPieceSize, 0, dstPieceSize - r)
      ctx.lineTo(0, r)
      ctx.quadraticCurveTo(0, 0, r, 0)
      ctx.closePath()
      ctx.clip()
      
      ctx.drawImage(img, sx, sy, srcPieceSize, srcPieceSize, 0, 0, dstPieceSize, dstPieceSize)
      
      ctx.strokeStyle = 'rgba(255,255,255,0.9)'
      ctx.lineWidth = 3
      ctx.stroke()
      
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => resolve(null)
    img.src = fullImageSrc
  })
}

async function startGame() {
  if (!canStart.value) return
  
  if (imageSource.value === 'models' || imageSource.value === 'ai') {
    gameImage.value = await generateModelImage(selectedModel.value)
  } else {
    gameImage.value = uploadedImage.value
  }
  
  phase.value = 'playing'
  moves.value = 0
  seconds.value = 0
  showComplete.value = false
  
  await nextTick()
  await initPuzzle()
  startTimer()
}

async function initPuzzle() {
  const grid = gridSize.value
  const size = pieceSizeValue.value
  
  pieces.splice(0, pieces.length)
  gridSlots.splice(0, gridSlots.length)
  
  // Create slots
  for (let row = 0; row < grid; row++) {
    for (let col = 0; col < grid; col++) {
      gridSlots.push({
        id: row * grid + col,
        row, col,
        x: col * size,
        y: row * size
      })
    }
  }
  
  // Create pieces (shuffled order)
  const piecesData = []
  for (let row = 0; row < grid; row++) {
    for (let col = 0; col < grid; col++) {
      piecesData.push({
        id: row * grid + col,
        correctRow: row,
        correctCol: col,
        isPlaced: false,
        imageUrl: null
      })
    }
  }
  
  // Shuffle
  for (let i = piecesData.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [piecesData[i], piecesData[j]] = [piecesData[j], piecesData[i]]
  }
  
  pieces.push(...piecesData)
  
  // Generate images for each piece
  for (const piece of pieces) {
    piece.imageUrl = await generatePieceImage(
      gameImage.value, 
      piece.correctRow, 
      piece.correctCol, 
      grid
    )
  }
}

function slotStyle(slot) {
  const size = pieceSizeValue.value
  return {
    width: size + 'px',
    height: size + 'px',
    left: slot.x + 'px',
    top: slot.y + 'px'
  }
}

function trayPieceStyle() {
  const size = pieceSizeValue.value
  return {
    width: size + 'px',
    height: size + 'px'
  }
}

function placedPieceStyle(piece) {
  const size = pieceSizeValue.value
  return {
    width: size + 'px',
    height: size + 'px',
    left: (piece.correctCol * size) + 'px',
    top: (piece.correctRow * size) + 'px'
  }
}

// Drag handling
function onPieceDown(e, piece) {
  e.preventDefault()
  
  draggingPiece.value = piece
  
  const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0
  const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0
  
  dragPosition.x = clientX
  dragPosition.y = clientY
  
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
}

function onPointerMove(e) {
  if (!draggingPiece.value) return
  
  const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0
  const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0
  
  dragPosition.x = clientX
  dragPosition.y = clientY
}

function onPointerUp(e) {
  if (!draggingPiece.value) return
  
  const piece = draggingPiece.value
  const size = pieceSizeValue.value
  
  // Check if dropped on board
  const board = puzzleBoardRef.value
  if (!board) {
    draggingPiece.value = null
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    return
  }
  
  const boardRect = board.getBoundingClientRect()
  const clientX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? dragPosition.x
  const clientY = e.clientY ?? e.changedTouches?.[0]?.clientY ?? dragPosition.y
  
  // Check if pointer is over the board
  if (
    clientX >= boardRect.left && 
    clientX <= boardRect.right && 
    clientY >= boardRect.top && 
    clientY <= boardRect.bottom
  ) {
    // Calculate which slot
    const boardX = clientX - boardRect.left
    const boardY = clientY - boardRect.top
    
    const dropCol = Math.floor(boardX / size)
    const dropRow = Math.floor(boardY / size)
    
    // Check if this is the correct position for this piece
    if (dropRow === piece.correctRow && dropCol === piece.correctCol) {
      // Place the piece!
      piece.isPlaced = true
      playSnapSound()
    }
  }
  
  moves.value++
  draggingPiece.value = null
  
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  
  checkComplete()
}

function playSnapSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 660
    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.15)
  } catch (e) { /* silent */ }
}

function checkComplete() {
  const allPlaced = pieces.every(p => p.isPlaced)
  if (allPlaced) {
    stopTimer()
    showComplete.value = true
    submitSession()
  }
}

function startTimer() {
  stopTimer()
  timerInterval = setInterval(() => {
    seconds.value++
  }, 1000)
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

async function submitSession() {
  try {
    await submitPuzzle({
      data: {
        roomSlug: slug,
        imageBase64: gameImage.value,
        solutionBase64: gameImage.value,
        durationSeconds: seconds.value,
        moves: moves.value,
        mode: difficulty.value,
        previewUsed: false,
        gridSize: gridSize.value,
        placedCount: pieces.length,
        declaredDone: true,
        success: true
      }
    })
  } catch (e) {
    console.error('Failed to submit puzzle session', e)
  }
}

function replayGame() {
  showComplete.value = false
  moves.value = 0
  seconds.value = 0
  initPuzzle()
  startTimer()
}

function chooseAnother() {
  showComplete.value = false
  phase.value = 'select'
  selectedModel.value = null
  uploadedImage.value = null
  stopTimer()
}

onMounted(() => {
  // Ready
})

onUnmounted(() => {
  stopTimer()
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
})
</script>

<script>
export default { name: 'PuzzleGame' }
</script>

<style scoped>
.puzzle-wrap {
  min-height: 100vh;
  padding: 16px;
  background: linear-gradient(135deg, #a5f3fc 0%, #c4b5fd 50%, #fda4af 100%);
  font-family: 'Vazirmatn', sans-serif;
}

.header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.back-btn {
  background: linear-gradient(135deg, #fff 0%, #f0f9ff 100%);
  border: 3px solid #38bdf8;
  border-radius: 999px;
  padding: 10px 20px;
  font-weight: 700;
  font-size: 14px;
  color: #0369a1;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(56, 189, 248, 0.3);
  transition: all 0.3s ease;
}
.back-btn:hover {
  transform: scale(1.05);
}

.title {
  font-size: 28px;
  background: linear-gradient(135deg, #7c3aed 0%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  flex: 1;
  text-align: center;
}

.diff-pills {
  display: flex;
  gap: 8px;
}

.diff-pill {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #b45309;
  border: none;
  border-radius: 999px;
  padding: 10px 18px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.diff-pill.active {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #fff;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.4);
}

/* Select Phase */
.select-phase {
  max-width: 800px;
  margin: 0 auto;
}

.source-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: center;
}

.source-tab {
  background: rgba(255,255,255,0.9);
  border: 3px solid transparent;
  border-radius: 16px;
  padding: 14px 20px;
  font-weight: 700;
  font-size: 15px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
}
.source-tab.active {
  border-color: #8b5cf6;
  color: #7c3aed;
  background: #fff;
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.3);
}

.models-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.model-card {
  background: #fff;
  border: 3px solid #e5e7eb;
  border-radius: 16px;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}
.model-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}
.model-card.selected {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.3);
}
.model-card.small {
  padding: 8px;
}

.model-preview {
  width: 80px;
  height: 80px;
  margin: 0 auto 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.model-card.small .model-preview {
  width: 60px;
  height: 60px;
}

.model-emoji {
  font-size: 48px;
}
.model-card.small .model-emoji {
  font-size: 32px;
}

.model-name {
  font-weight: 700;
  color: #374151;
  font-size: 14px;
}

/* AI Chat */
.ai-chat {
  background: linear-gradient(135deg, #fff 0%, #f0fdf4 100%);
  border: 3px solid #34d399;
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 20px;
}

.chat-messages {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 12px;
}

.chat-msg {
  margin-bottom: 10px;
  display: flex;
}
.chat-msg.ai {
  justify-content: flex-start;
}
.chat-msg.kid {
  justify-content: flex-end;
}

.msg-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
}
.chat-msg.ai .msg-bubble {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
}
.chat-msg.kid .msg-bubble {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #166534;
}

.chat-input-row {
  display: flex;
  gap: 8px;
}

.chat-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #d1d5db;
  border-radius: 999px;
  font-size: 14px;
  outline: none;
}
.chat-input:focus {
  border-color: #8b5cf6;
}

.chat-send {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
}
.chat-send:disabled {
  opacity: 0.6;
}

.suggested-models {
  margin-top: 16px;
}

.suggest-label {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
}

.models-row {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Upload */
.upload-zone {
  margin-bottom: 20px;
}

.file-input {
  display: none;
}

.upload-box {
  background: linear-gradient(135deg, #fff 0%, #fef3c7 100%);
  border: 4px dashed #fbbf24;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-placeholder {
  color: #92400e;
}

.upload-icon {
  font-size: 64px;
  display: block;
  margin-bottom: 12px;
}

.upload-preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 12px;
}

/* Start Button */
.start-btn {
  display: block;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: 18px 32px;
  background: linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 20px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
}
.start-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Game Phase */
.game-phase {
  max-width: 500px;
  margin: 0 auto;
}

.game-stats {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.stat-box {
  background: linear-gradient(135deg, #fff 0%, #fef3c7 100%);
  border: 2px solid #fbbf24;
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-icon {
  font-size: 20px;
}

.stat-value {
  font-weight: 700;
  color: #92400e;
  font-size: 16px;
}

.preview-btn {
  background: linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%);
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 700;
  color: #5b21b6;
  cursor: pointer;
  user-select: none;
}

.game-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.puzzle-board {
  position: relative;
  background: linear-gradient(135deg, #fff 0%, #f0fdf4 100%);
  border: 6px solid #8b5cf6;
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
}

.puzzle-slot {
  position: absolute;
  border: 2px dashed rgba(139, 92, 246, 0.4);
  border-radius: 6px;
  background: rgba(139, 92, 246, 0.08);
  box-sizing: border-box;
}

/* Pieces tray - FLEXBOX GRID layout */
.pieces-tray {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  background: linear-gradient(135deg, #fff 0%, #fef3c7 100%);
  border: 4px solid #fbbf24;
  border-radius: 16px;
  padding: 16px;
  min-height: 100px;
  width: 100%;
  max-width: 360px;
}

.tray-piece {
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  cursor: grab;
  touch-action: none;
  overflow: hidden;
  flex-shrink: 0;
}
.tray-piece:hover {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  transform: scale(1.05);
}
.tray-piece.dragging {
  opacity: 0.3;
}

.puzzle-piece.placed {
  position: absolute;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.piece-image {
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* Dragging piece - follows cursor */
.dragging-piece {
  position: fixed;
  pointer-events: none;
  z-index: 9999;
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}
.dragging-piece .piece-image {
  width: 100%;
  height: 100%;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  z-index: 100;
}

.preview-image {
  max-width: 95%;
  max-height: 95%;
  border-radius: 8px;
}

/* Complete Popup */
.complete-overlay {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(99, 102, 241, 0.9) 50%, rgba(236, 72, 153, 0.9) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
}

.complete-popup {
  background: #fff;
  border-radius: 32px;
  padding: 40px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 24px 60px rgba(0,0,0,0.3);
}

.confetti {
  font-size: 80px;
  animation: confetti-bounce 1s ease-in-out infinite;
}
@keyframes confetti-bounce {
  0%, 100% { transform: translateY(0) rotate(-5deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
}

.complete-popup h2 {
  font-size: 24px;
  background: linear-gradient(135deg, #7c3aed 0%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 16px 0 8px;
}

.complete-stats {
  color: #6b7280;
  font-size: 16px;
  margin-bottom: 24px;
}

.complete-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-btn {
  padding: 14px 24px;
  border: none;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.action-btn.replay {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  color: #fff;
}
.action-btn.choose {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: #fff;
}
.action-btn.back {
  background: linear-gradient(135deg, #f472b6 0%, #ec4899 100%);
  color: #fff;
}

@media (max-width: 600px) {
  .header {
    flex-direction: column;
    text-align: center;
  }
  .title {
    order: -1;
    width: 100%;
  }
  .diff-pills {
    justify-content: center;
  }
  .models-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .model-preview {
    width: 60px;
    height: 60px;
  }
  .model-emoji {
    font-size: 32px;
  }
}
</style>
